import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Button, formatPrice, timestampFormatter } from '@retainerclub/common';
import { useAccount, useLocale } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router';
import { tierChangeStatement } from '../../../../SubscriptionDisclosure/helpers/tierChangeStatement';
import EffectiveDateChoice from './EffectiveDateChoice';
import { resolveEffectiveDateOptions } from './effectiveDateOptions';
import SubscriptionActionAlert from './SubscriptionActionAlert';

function ManageSubscriptionModalBodyConfirmation({
  selectedAction,
  handleConfirmAction,
  setCurrentView,
  isLoading,
  subscription,
  now,
  setNow,
  actionError,
}) {
  const { locale } = useLocale();
  const { accountCreditCards } = useAccount();
  const { type, tier } = selectedAction;
  // The agreement is bound to the exact statement that was checked: switching
  // between "now" and "on renewal" rewrites the statement, so the box clears.
  const [agreedStatement, setAgreedStatement] = useState(null);

  // The same consent the customer gave at enrollment, for the new terms: a
  // statement of the tier, price and effective date, agreed by checkbox. It
  // is written to the consent ledger with the change.
  const priceText = formatPrice(
    tier.overPrice ? tier.price : tier.product.price,
    locale,
  );
  const statement = tierChangeStatement({
    fromName: subscription.name,
    toName: tier.product.name,
    priceText,
    renewalIntervalMonths: tier.product.renewalIntervalMonths,
    now,
    renewalDate: timestampFormatter(subscription.renewalAt, false),
  });
  const agreed = agreedStatement === statement;

  const currentCreditCard = useMemo(
    () => accountCreditCards.find((card) => card.defaultCard),
    [accountCreditCards],
  );

  const effectiveDateOptions = useMemo(
    () => resolveEffectiveDateOptions(subscription),
    [subscription],
  );
  const { canChooseNow, canChooseOnRenewal, hasNoEffectiveDateOption } =
    effectiveDateOptions;

  // `now` defaults to false in the parent, so a past-due subscription would submit "on renewal" -
  // the one value the backend refuses - purely because the customer never touched a radio that
  // isn't rendered. Whenever only one option is available, make the state match it.
  useEffect(() => {
    if (canChooseNow && !canChooseOnRenewal) {
      setNow(true);
    } else if (canChooseOnRenewal && !canChooseNow) {
      setNow(false);
    }
  }, [canChooseNow, canChooseOnRenewal, setNow]);

  return (
    <div className="space-y-6">
      <div className="text-center">
        {currentCreditCard ? (
          <CheckCircleIcon
            className={`mx-auto h-12 w-12 ${
              type === 'upgrade' ? 'text-green-600' : 'text-yellow-600'
            }`}
          />
        ) : (
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-rccallouts-warning" />
        )}

        <h3 className="mt-4 font-semibold text-gray-900 text-lg">
          Confirm <span className="capitalize">{type}</span>
        </h3>
        <p className="mt-2 text-gray-600 text-sm">
          Switching to{' '}
          <span className="font-semibold">{tier.product.name}</span>
          for{' '}
          <span className="font-semibold">
            {formatPrice(
              tier.overPrice ? tier.price : tier.product.price,
              locale,
            )}
          </span>
          .
        </p>
      </div>

      {currentCreditCard ? (
        <>
          <EffectiveDateChoice
            subscription={subscription}
            tier={tier}
            now={now}
            setNow={setNow}
            options={effectiveDateOptions}
          />
          <div
            className={`rounded-lg border ${
              type === 'upgrade'
                ? 'border-green-200 bg-green-50'
                : 'border-yellow-200 bg-yellow-50'
            } p-4`}
          >
            <h4
              className={`mb-2 font-medium ${
                type === 'upgrade' ? 'text-green-900' : 'text-yellow-900'
              }`}
            >
              What happens next?
            </h4>
            <ul
              className={`space-y-1 ${
                type === 'upgrade' ? 'text-green-800' : 'text-yellow-800'
              } list-disc pl-8 text-sm`}
            >
              <li>
                Your card ending in{' '}
                <span className="font-semibold">
                  {currentCreditCard.maskedNumber}
                </span>{' '}
                will be charged {now ? 'today' : 'on your renewal date'}.
              </li>
              <li>You can change plans any time</li>
              <li>We&apos;ll handle your retainer shipments automatically</li>
            </ul>
          </div>

          <div className="space-y-3 rounded-lg border border-gray-200 p-4">
            <label className="flex gap-x-3" htmlFor="tier-change-agreed">
              <input
                id="tier-change-agreed"
                type="checkbox"
                className="mt-1 size-4 rounded border-rcgray-600 text-rcprimary-400 focus:ring-rcprimary-400"
                checked={agreed}
                onChange={(event) =>
                  setAgreedStatement(event.target.checked ? statement : null)
                }
              />
              <span className="text-gray-700 text-sm">{statement}</span>
            </label>
          </div>
        </>
      ) : (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <h4 className="mb-2 font-medium text-red-900">
            You need to have a valid credit card on file to {type}
          </h4>
          <p className="text-gray-600 text-sm">
            You can add a credit card to your account by visting the{' '}
            <NavLink to="/payment-methods" className="text-red-800 underline">
              Payment Methods
            </NavLink>{' '}
            page.
          </p>
        </div>
      )}

      <SubscriptionActionAlert actionError={actionError} />

      <div className="flex space-x-4">
        <Button
          name="Cancel"
          size="lg"
          rounded
          color="grayOutline"
          onClick={() => setCurrentView('main')}
          className="flex-1 justify-center"
        />
        <Button
          name="Confirm"
          size="lg"
          rounded
          color={type === 'upgrade' ? 'green' : 'yellow'}
          onClick={() => handleConfirmAction({ agreed })}
          disabled={
            isLoading ||
            !currentCreditCard ||
            hasNoEffectiveDateOption ||
            !agreed
          }
          className="flex-1 justify-center"
        />
      </div>
    </div>
  );
}

ManageSubscriptionModalBodyConfirmation.propTypes = {
  selectedAction: PropTypes.object.isRequired,
  handleConfirmAction: PropTypes.func.isRequired,
  setCurrentView: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  subscription: PropTypes.object.isRequired,
  now: PropTypes.bool.isRequired,
  setNow: PropTypes.func.isRequired,
  actionError: PropTypes.object,
};

export default ManageSubscriptionModalBodyConfirmation;
